import { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import Input from "../../../../components/form/Input";
import Radio from "../../../../components/form/Radio";
import Button from "../../../../components/form/Button";
import Form from "../../../../components/form/Form";
import useForm from "../../../../hooks/useForm";

import { organizationAddEdit } from "../../../../services/organizationService";
import Select from "../../../../components/form/select/Select";
import { useTranslation } from "react-i18next";

const validationRules = {
  label: { required: true, message: "Please enter label" },
  amount: { required: true, message: "Please enter amount" },
};

export default function TaxSetting({ data = {}, organizationDetailsRefetch }) {
  const { t } = useTranslation();
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    validationRules: validationRules,
    initialState: { taxAmountDisplay: "show", promoterFee: 0 },
  });

  useEffect(() => {
    const feeSetting = data?.feeSetting || [];
    const tax = data?.taxData?.[0] || {};

    const percentFee = feeSetting.find((f) => f.type === "percent")?.amount;
    const fixedFee = feeSetting.find((f) => f.type === "fixed")?.amount;

    setFieldsValue({
      promoterFee:
        typeof data?.promoterFee === "number"
          ? String(data?.promoterFee * 100)
          : "",
      percentage:
        typeof percentFee === "number" ? String(percentFee * 100) : "",
      fixedAmount: typeof fixedFee === "number" ? String(fixedFee) : "",

      taxTreatment: tax?.inclusive ? "inclusive" : "exclusive",
      amount: typeof tax?.rate === "number" ? String(tax.rate * 100) : "",
      label: tax?.name || "",
      taxAmountDisplay: tax?.displayToBuyer
        ? tax?.displayToBuyer === "yes"
          ? "show"
          : "hide"
        : "show",
    });
  }, [data]);

  const mutation = useMutation({
    mutationFn: (payload) => organizationAddEdit(payload),
    onSuccess: () => {
      toast.success("Settings saved successfully");
      organizationDetailsRefetch?.();
    },
    onError: () => toast.error("Failed to save settings"),
  });

  const onSubmit = (formData) => {
    const feeSetting = [];

    const percent = parseFloat(formData.percentage);
    const fixed = parseFloat(formData.fixedAmount);

    if (!isNaN(percent) && percent >= 0) {
      feeSetting.push({ type: "percent", amount: percent / 100 });
    }

    if (!isNaN(fixed) && fixed >= 0) {
      feeSetting.push({ type: "fixed", amount: fixed });
    }

    const payload = {
      id: data?._id,
      feeSetting,
      promoterFee: Number(formData?.promoterFee || 0) / 100,
      taxData: {
        ...(formData.label && {
          name: formData.label,
        }),
        ...(formData.amount && {
          rate:
            (values.taxTreatment === "inclusive"
              ? 0
              : Number(formData.amount || 0)) / 100,
        }),
        inclusive: formData.taxTreatment === "inclusive",
        // ...(formData.taxTreatment === "inclusive" && {
        //   displayToBuyer: formData.taxAmountDisplay === "show" ? "yes" : "no",
        // }),
      },
    };

    mutation.mutate(payload);
  };

  return (
    <Form
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      className="mx-auto bg-white shadow py-4 px-4 rounded-md"
    >
      {/* <div className="mb-4 border-l-4 border-yellow-500 bg-red-50 p-3 text-sm text-yellow-700 rounded">
        <i className="fa-regular text-xl fa-triangle-exclamation mr-2"></i>
        These are default settings and can also be overridden per event.
      </div> */}

      <div className="py-4 md:py-8 px-3 md:px-6 rounded-md space-y-4 border border-gray-200">
        <h2 className="text-xl md:text-2xl text-deep-purple font-bold font-Montserrat">
          {t("Transaction fees")}
        </h2>
        <p className="mb-6 text-sm text-text-secondary font-[300]">
          {t(
            "Transaction fees can be charged once per order. Set a fixed amount, a percentage of the order amount, or a combination of both. Set to zero to not charge a transaction fee."
          )}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Input
            label={t("Percentage")}
            inputType="number"
            inputSuffix="%"
            inputName="percentage"
            value={values?.percentage || ""}
            onChange={handleChange}
            labelClasses={
              "text-xs font-medium text-text-disabled mb-1 font-CanelaDeck !text-lg !text-deep-purple !mb-2"
            }
            inputClasses={
              "px-3 text-sm text-deep-purple !font-medium !h-12 !bg-surface-04 !border-surface-05"
            }
            {...formValidate(errors, "percentage")}
          />
          <Input
            label={t("Fixed amount")}
            inputType="number"
            // prefix="$"
            inputName="fixedAmount"
            value={values?.fixedAmount || ""}
            onChange={handleChange}
            labelClasses={
              "text-xs font-medium text-text-disabled mb-1 font-CanelaDeck !text-lg !text-deep-purple !mb-2"
            }
            inputClasses={
              "px-3 text-sm text-deep-purple !font-medium !h-12 !bg-surface-04 !border-surface-05"
            }
            {...formValidate(errors, "fixedAmount")}
          />
        </div>

        {/* <div>
          <Input
            label="Promoter fee"
            inputType="number"
            inputSuffix="%"
            inputName="promoterFee"
            value={values?.promoterFee}
            onChange={handleChange}
            labelClasses={
              "text-xs font-medium text-text-disabled mb-1 font-CanelaDeck !text-lg !text-deep-purple !mb-2"
            }
            inputClasses={
              "px-3 text-sm text-deep-purple !font-medium !h-12 !bg-surface-04 !border-surface-05"
            }
            {...formValidate(errors, "promoterFee")}
          />
        </div> */}

        <h2 className="font-medium font-CanelaDeck !text-lg text-deep-purple mb-2">
          {t("Sales tax")}
        </h2>
        {/* <div className="mb-4 border-l-4 border-orange-500 bg-red-50 p-3 text-sm text-orange-700 rounded">
        <i className="fa-regular text-xl fa-triangle-exclamation mr-2"></i>
        DISCLAIMER: Tax compliance is your responsibility. Please consult a
        professional.
      </div> */}

        <div className="mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            {t("Tax treatment")}
            <i className="fa-regular text-xl fa-circle-question ml-1 text-primary" />
          </label>
          <div className="flex flex-col gap-3">
            <Radio
              value="exclusive"
              radioName="taxTreatment"
              checked={values.taxTreatment === "exclusive"}
              onChange={() => {
                setFieldsValue({ taxTreatment: "exclusive" });
              }}
              radioLabel={t(
                "Exclusive – additional tax will be charged at the rate set"
              )}
              divClass={"!border !border-primary peer-checked:text-primary"}
              radioLableClass={"font-medium"}
            />
            <Radio
              value="inclusive"
              radioName="taxTreatment"
              checked={values.taxTreatment === "inclusive"}
              onChange={() => {
                setFieldsValue({ taxTreatment: "inclusive" });
              }}
              radioLabel={t(
                "Inclusive – assumes the tax is already included in any prices set"
              )}
              divClass={"!border !border-primary peer-checked:text-primary"}
              radioLableClass={"font-medium"}
            />
          </div>
        </div>
        {/* {values.taxTreatment === "inclusive" && (
          <div className="mb-6">
            <Select
              label="Tax amount display"
              dropdownData={[
                { name: "Show tax amounts to ticket buyers", value: "show" },
                { name: "Hide tax amounts from ticket buyers", value: "hide" },
              ]}
              selectedValue={values?.taxAmountDisplay}
              onChange={(val) => setFieldsValue({ taxAmountDisplay: val })}
              placeholder="Select tax display"
              labelClasses={"text-lg text-deep-purple mb-2 "}
              dropdownClass={"w-full"}
              dropdownButtonClass={
                "rounded-md !font-medium !text-deep-purple !bg-surface-04 !border-surface-05"
              }
            />
          </div>
        )} */}

        {values.taxTreatment === "exclusive" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Input
              label={t("Amount")}
              inputType="number"
              inputSuffix="%"
              inputName="amount"
              value={values?.amount || ""}
              onChange={handleChange}
              labelClasses={
                "text-xs font-medium text-text-disabled mb-1 font-CanelaDeck !text-lg !text-deep-purple !mb-2"
              }
              inputClasses={
                "px-3 text-sm text-deep-purple !font-medium !h-12 !bg-surface-04 !border-surface-05"
              }
              {...formValidate(errors, "amount")}
            />
            <Input
              label={t("Label")}
              inputName="label"
              value={values?.label || ""}
              onChange={handleChange}
              labelClasses={
                "text-xs font-medium text-text-disabled mb-1 font-CanelaDeck !text-lg !text-deep-purple !mb-2"
              }
              inputClasses={
                "px-3 text-sm text-deep-purple !font-medium !h-12 !bg-surface-04 !border-surface-05"
              }
              {...formValidate(errors, "label")}
            />
          </div>
        )}
      </div>
      <div className="flex mt-7 gap-3 justify-end bg-magnolia py-4 sticky bottom-0 right-0 z-[2] pr-4 md:pr-6">
        <Button
          buttonType="submit"
          buttonLabel={t("Save")}
          loading={mutation?.isPending}
          buttonClasses={
            "bg-primary-gradient hover:opacity-90 transition-all duration-300 !text-white"
          }
          buttonLabelClasses={
            "!text-white font-semibold text-base rounded-full"
          }
        />
      </div>
    </Form>
  );
}
